import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Check } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/shared/utils/core";

export interface Option {
  label: string;
  value: string;
}

interface CustomMultiSelectProps {
  options: Option[];
  selected: Option[];
  onChange: (options: Option[]) => void;
  className?: string;
  triggerClassName?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  maxHeight?: string;
}

export function CustomMultiSelect({
  options,
  selected,
  onChange,
  className,
  triggerClassName,
  placeholder = "Select items...",
  disabled = false,
  maxHeight = "max-h-64",
}: CustomMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleToggleOption = (option: Option) => {
    const isSelected = selected.some((s) => s.value === option.value);
    if (isSelected) {
      onChange(selected.filter((s) => s.value !== option.value));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleRemoveOption = (optionToRemove: Option) => {
    onChange(selected.filter((s) => s.value !== optionToRemove.value));
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <Button asChild variant="outline" disabled={disabled}>
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          className={cn(
            "w-full justify-between bg-white hover:bg-white border-primary hover:border-strong min-h-[40px]",
            isOpen && "border-focus",
            disabled && "pointer-events-none opacity-50",
            triggerClassName,
          )}
        >
          <div className="flex-1 flex flex-wrap items-center gap-1 min-w-0">
            {selected.length === 0 ? (
              <span className="text-left text-stone-500">{placeholder}</span>
            ) : (
              <>
                {/* Show first 2 selected items on desktop, first 1 on mobile */}
                {selected.slice(0, 2).map((option, index) => (
                  <Badge
                    key={option.value}
                    variant="stone"
                    className={cn("min-w-0", index === 1 && "hidden md:inline-flex")}
                  >
                    <span className="truncate">{option.label}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveOption(option);
                      }}
                      aria-label={`Remove ${option.label}`}
                      className="ml-1 flex flex-shrink-0 items-center text-stone-500 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {/* Show +X more badge if there are more than 2 items */}
                {selected.length > 2 && <Badge variant="stone">+{selected.length - 2} more</Badge>}
              </>
            )}
          </div>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 opacity-50 transition-transform duration-200 ml-2",
              isOpen && "rotate-180",
            )}
          />
        </div>
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-[100] bg-white border border-primary rounded-md shadow-lg">
          {/* Options List */}
          <div className={cn("overflow-y-auto", maxHeight)}>
            {options.length === 0 ? (
              <div className="p-3 text-center text-sm text-gray-500">No options available</div>
            ) : (
              options.map((option) => {
                const isSelected = selected.some((s) => s.value === option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleToggleOption(option)}
                    className={cn(
                      "w-full flex items-center px-3 py-2 text-left text-sm hover:bg-subtle transition-colors",
                      isSelected && "bg-brand-subtle hover:bg-brand-subtle",
                    )}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 border-2 rounded-sm mr-3 flex items-center justify-center",
                        isSelected ? "border-brand bg-brand" : "border-primary",
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span
                      className={cn(
                        "flex-1",
                        isSelected ? "text-primary font-medium" : "text-secondary",
                      )}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
